import { Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import type { Overview } from '../types'
import { monthLabel, useFinanceLookups } from '../ui'
import { downloadCsv } from '../csv'
import { CategoryDot, Money, ReviewBadge, Section } from './FinanceBits'

export function CategoryDetail({ rows, month }: { rows: Overview['categoryDetail']; month: string }) {
  const { t } = useTranslation()
  const { cat } = useFinanceLookups()
  const cols = ['category', 'amount', 'documented', 'missing', 'count', 'missingCount', 'review'] as const

  const exportCsv = () =>
    downloadCsv(
      `gider-kategorileri-${month}.csv`,
      cols.map((c) => t(`finance.overview.detail.${c}`)),
      rows.map((r) => [cat.get(r.categoryId)?.name ?? '', r.amount, r.documented, r.missing, String(r.count), String(r.missingCount), t(`finance.review.${r.review}`)]),
    )

  return (
    <Section
      title={t('finance.overview.detailTitle', { period: monthLabel(month) })}
      bodyClassName="p-0"
      actions={
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!rows.length}>
          <Download />
          {t('finance.exportCsv')}
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('finance.noData')}</p>
      ) : (
        <div className="@container">
          <ul className="divide-y @3xl:hidden">
            {rows.map((r) => {
              const c = cat.get(r.categoryId)
              return (
                <li key={r.categoryId} className="space-y-2 p-4">
                  <div className="flex items-center gap-2">
                    <CategoryDot color={c?.color ?? 1} />
                    <span className="min-w-0 flex-1 truncate font-medium">{c?.name}</span>
                    <ReviewBadge status={r.review} />
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <dt className="text-muted-foreground">{t('finance.overview.detail.amount')}</dt>
                      <dd className="text-sm font-semibold"><Money value={r.amount} /></dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('finance.overview.detail.missing')}</dt>
                      <dd className="text-sm font-semibold"><Money value={r.missing} className={r.missingCount ? 'text-destructive' : ''} /></dd>
                    </div>
                    <div className="col-span-2 text-muted-foreground">{t('finance.overview.detail.counts', { count: r.count, missing: r.missingCount })}</div>
                  </dl>
                </li>
              )
            })}
          </ul>
          <div className="hidden @3xl:block">
            <Table>
              <THead>
                <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
                  {cols.map((c) => (
                    <TH key={c} className={c === 'category' || c === 'review' ? '' : 'text-right'}>
                      {t(`finance.overview.detail.${c}`)}
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <TR key={r.categoryId}>
                    <TD>
                      <span className="flex items-center gap-2 font-medium">
                        <CategoryDot color={cat.get(r.categoryId)?.color ?? 1} />
                        {cat.get(r.categoryId)?.name}
                      </span>
                    </TD>
                    <TD className="text-right"><Money value={r.amount} /></TD>
                    <TD className="text-right"><Money value={r.documented} /></TD>
                    <TD className="text-right"><Money value={r.missing} className={r.missingCount ? 'text-destructive' : ''} /></TD>
                    <TD className="text-right tabular-nums">{r.count}</TD>
                    <TD className="text-right tabular-nums">{r.missingCount}</TD>
                    <TD><ReviewBadge status={r.review} /></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </div>
      )}
    </Section>
  )
}
